import React, { useCallback, useRef, useState } from 'react';
import { useNavigate, useParams, Link, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useData } from '../../context/DataContext';
import { uploadApi, garageApi } from '../../api/client';
import type { GarageMod, TrackSession } from '../../types';
import { GARAGE_MOD_CATEGORIES, TRACK_SESSION_EVENT_TYPES, POWERTRAIN_TYPES } from '../../types';
import { CAR_TYPES, PERFORMANCE_TYPES, ENGINE_TYPES } from '../../constants/spotCategories';
import type { GarageCarNavState } from '../../navigation/garageCarNav';
import { useCarMakes } from '../../hooks/useCarMakes';
import { trackEvent } from '../../analytics';
import styles from './GarageCarForm.module.css';

const MAX_MOD_IMAGES = 4;
const CURRENT_MAKE_VALUE = '__current__';
const OTHER_MAKE_VALUE = '__other__';

/** YouTube URL helpers */
function isYoutubeUrl(url: string): boolean {
  if (!url || typeof url !== 'string') return false;
  return /youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\//i.test(url.trim());
}

function getYoutubeVideoId(url: string): string | null {
  if (!isYoutubeUrl(url)) return null;
  const u = url.trim();
  const m = u.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([a-zA-Z0-9_-]{11})/i);
  return m ? m[1] : null;
}

function getYoutubeThumbnail(url: string): string {
  const id = getYoutubeVideoId(url);
  return id ? `https://img.youtube.com/vi/${id}/mqdefault.jpg` : '';
}

function getYoutubeEmbedUrl(url: string): string {
  const id = getYoutubeVideoId(url);
  return id ? `https://www.youtube.com/embed/${id}?autoplay=1` : '';
}

/** Format YYYY-MM-DD or ISO date string to local MM/DD/YYYY */
function formatDateLocal(dateStr: string | undefined): string {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  if (Number.isNaN(d.getTime())) return dateStr;
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  const year = d.getFullYear();
  return `${month}/${day}/${year}`;
}

/** Normalize date string to YYYY-MM-DD for input[type="date"] */
function toDateInputValue(dateStr: string | undefined): string {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  if (Number.isNaN(d.getTime())) return '';
  return d.toISOString().slice(0, 10);
}

/** Parse lap time display M:SS or M:SS.xxx to seconds for API */
function parseLapTimeDisplay(display: string): number | null {
  const trimmed = display.trim();
  if (!trimmed) return null;
  const match = trimmed.match(/^(\d+):(\d{1,2})(?:\.(\d+))?$/);
  if (!match) return null;
  const minutes = parseInt(match[1], 10);
  const secs = parseInt(match[2], 10);
  const frac = match[3] ? parseFloat('0.' + match[3].padEnd(3, '0').slice(0, 3)) : 0;
  if (secs >= 60) return null;
  return minutes * 60 + secs + frac;
}

function performanceSpecsFromForm(
  zeroToSixty: string,
  hp: string,
  torque: string,
  quarterMile: string,
  topSpeed: string,
  curbWeight: string
): Record<string, string> {
  const out: Record<string, string> = {};
  if (zeroToSixty.trim()) out['0-60'] = zeroToSixty.trim();
  if (hp.trim()) out['HP'] = hp.trim();
  if (torque.trim()) out['Torque'] = torque.trim();
  if (quarterMile.trim()) out['Quarter Mile'] = quarterMile.trim();
  if (topSpeed.trim()) out['Top Speed'] = topSpeed.trim();
  if (curbWeight.trim()) out['Curb weight'] = curbWeight.trim();
  return out;
}

export function GarageCarForm() {
  const navigate = useNavigate();
  const location = useLocation();
  const returnNavState = (location.state as GarageCarNavState | null | undefined) ?? undefined;
  const { id } = useParams<{ id: string }>();
  const { user, isAuthenticated } = useAuth();
  const { garageCars, addGarageCar, updateGarageCar, refreshGarage } = useData();
  const hasTrackedStartRef = useRef(false);

  const existing = id && id !== 'new' ? garageCars.find((c) => c.id === id) : null;
  const isEdit = !!existing;
  const myGarageCount = user ? garageCars.filter((car) => car.ownerId === user.id).length : 0;

  const [name, setName] = useState(existing?.name ?? '');
  const [make, setMake] = useState(existing?.car.make ?? '');
  const [model, setModel] = useState(existing?.car.model ?? '');
  const [year, setYear] = useState(existing?.year ?? existing?.car.year ?? '');
  const [bodyStyle, setBodyStyle] = useState(existing?.car.bodyStyle ?? '');
  const [powertrainType, setPowertrainType] = useState(existing?.car.powertrainType ?? 'gas');
  const [performanceType, setPerformanceType] = useState(existing?.car.performanceType ?? '');
  const [trim, setTrim] = useState(existing?.trim ?? existing?.car.trim ?? '');
  const [engine, setEngine] = useState(existing?.engine ?? '');
  const [engineType, setEngineType] = useState(() => {
    const s = existing?.performanceSpecs;
    if (s && typeof s === 'object' && !Array.isArray(s) && typeof s['Engine Type'] === 'string') return s['Engine Type'];
    return '';
  });
  const [torque, setTorque] = useState(() => {
    const s = existing?.performanceSpecs;
    if (s && typeof s === 'object' && !Array.isArray(s) && typeof s['Torque'] === 'string') return s['Torque'];
    return '';
  });
  const [transmission, setTransmission] = useState(() => {
    const s = existing?.performanceSpecs;
    if (s && typeof s === 'object' && !Array.isArray(s) && typeof s['Transmission'] === 'string') return s['Transmission'];
    return '';
  });
  const [spec0To60, setSpec0To60] = useState(() => {
    const s = existing?.performanceSpecs;
    if (s && typeof s === 'object' && !Array.isArray(s) && typeof s['0-60'] === 'string') return s['0-60'];
    return '';
  });
  const [quarterMile, setQuarterMile] = useState(() => {
    const s = existing?.performanceSpecs;
    if (s && typeof s === 'object' && !Array.isArray(s) && typeof s['Quarter Mile'] === 'string') return s['Quarter Mile'];
    return '';
  });
  const [topSpeed, setTopSpeed] = useState(() => {
    const s = existing?.performanceSpecs;
    if (s && typeof s === 'object' && !Array.isArray(s) && typeof s['Top Speed'] === 'string') return s['Top Speed'];
    return '';
  });
  const [specHp, setSpecHp] = useState(() => {
    const s = existing?.performanceSpecs;
    if (s && typeof s === 'object' && !Array.isArray(s) && typeof s['HP'] === 'string') return s['HP'];
    return '';
  });
  const [curbWeight, setCurbWeight] = useState(() => {
    const s = existing?.performanceSpecs;
    if (s && typeof s === 'object' && !Array.isArray(s) && typeof s['Curb weight'] === 'string') return s['Curb weight'];
    return '';
  });
  const [colorCode, setColorCode] = useState(existing?.colorCode ?? '');
  const [colorName, setColorName] = useState(existing?.colorName ?? '');
  const [vin, setVin] = useState(existing?.vin ?? '');
  const [currentOdometer, setCurrentOdometer] = useState(
    existing?.currentOdometer != null ? String(existing.currentOdometer) : ''
  );
  const [averageMilesPerMonth, setAverageMilesPerMonth] = useState(
    existing?.averageMilesPerMonth != null ? String(existing.averageMilesPerMonth) : ''
  );
  const [maintenanceRemindersEnabled, setMaintenanceRemindersEnabled] = useState(
    existing?.maintenanceRemindersEnabled ?? true
  );
  type ImageEntry = { preview: string; caption: string; file?: File; url?: string };
  const [imageEntries, setImageEntries] = useState<ImageEntry[]>(() => {
    const imgs = existing?.images ?? existing?.photos?.map((url) => ({ url, caption: '' })) ?? [];
    return imgs.map((img) => ({
      preview: typeof img === 'string' ? img : img.url,
      caption: typeof img === 'string' ? '' : img.caption ?? '',
      url: typeof img === 'string' ? img : img.url,
    }));
  });
  const photoInputRef = useRef<HTMLInputElement>(null);
  const MAX_IMAGES = 10;
  const [isPastCar, setIsPastCar] = useState(existing?.isPastCar ?? false);
  const [isPrimary, setIsPrimary] = useState(existing?.isPrimary ?? false);
  const [acquiredDate, setAcquiredDate] = useState(existing?.acquiredDate ?? '');
  const [soldDate, setSoldDate] = useState(existing?.soldDate ?? '');
  const [forSale, setForSale] = useState(existing?.forSale ?? false);
  const [saleSellingDetails, setSaleSellingDetails] = useState(existing?.saleSellingDetails ?? '');
  const [saleCarCondition, setSaleCarCondition] = useState(existing?.saleCarCondition ?? '');
  const [saleCarfaxLink, setSaleCarfaxLink] = useState(existing?.saleCarfaxLink ?? '');
  const [saleCarFeatures, setSaleCarFeatures] = useState(existing?.saleCarFeatures ?? '');
  const [salePrice, setSalePrice] = useState(existing?.salePrice ?? '');
  const [saleMilesDriven, setSaleMilesDriven] = useState(
    existing?.saleMilesDriven != null ? String(existing.saleMilesDriven) : ''
  );
  const [submitting, setSubmitting] = useState(false);
  const [useCustomMake, setUseCustomMake] = useState(false);
  const { carMakes, loading: carMakesLoading, error: carMakesError } = useCarMakes();

  // Mod modal state
  const [modModalOpen, setModModalOpen] = useState(false);
  const [editingMod, setEditingMod] = useState<GarageMod | null>(null);
  const [modCategory, setModCategory] = useState<string>(GARAGE_MOD_CATEGORIES[0]);
  const [modTitle, setModTitle] = useState('');
  const [modDetails, setModDetails] = useState('');
  const [modDateCompleted, setModDateCompleted] = useState('');
  type ModImageEntry = { preview: string; caption: string; file?: File; url?: string };
  const [modImageEntries, setModImageEntries] = useState<ModImageEntry[]>(() =>
    Array(MAX_MOD_IMAGES).fill(null).map(() => ({ preview: '', caption: '', url: '' }))
  );
  const [modSubmitting, setModSubmitting] = useState(false);
  const modPhotoInputRef = useRef<HTMLInputElement>(null);
  const [expandedModMedia, setExpandedModMedia] = useState<string | null>(null);

  // Track session modal state
  const [sessionModalOpen, setSessionModalOpen] = useState(false);
  const [editingSession, setEditingSession] = useState<TrackSession | null>(null);
  const [sessionVenueName, setSessionVenueName] = useState('');
  const [sessionDate, setSessionDate] = useState('');
  const [sessionEventType, setSessionEventType] = useState<string>(TRACK_SESSION_EVENT_TYPES[0]);
  const [sessionEventName, setSessionEventName] = useState('');
  const [sessionBestLap, setSessionBestLap] = useState('');
  const [sessionSlowestLap, setSessionSlowestLap] = useState('');
  const [sessionLapCount, setSessionLapCount] = useState('');
  const [sessionTiresUsed, setSessionTiresUsed] = useState('');
  const [sessionNotes, setSessionNotes] = useState('');
  const [sessionSubmitting, setSessionSubmitting] = useState(false);

  const trackGarageStart = useCallback((source: string) => {
    if (isEdit || hasTrackedStartRef.current) return;
    hasTrackedStartRef.current = true;
    const isFirstGarage = myGarageCount === 0;
    trackEvent('garage_creation_started', { source, first_garage: isFirstGarage });
    if (isFirstGarage) {
      trackEvent('first_garage_started', { source });
    }
  }, [isEdit, myGarageCount]);

  const openAddSessionModal = () => {
    setEditingSession(null);
    setSessionVenueName('');
    setSessionDate('');
    setSessionEventType(TRACK_SESSION_EVENT_TYPES[0]);
    setSessionEventName('');
    setSessionBestLap('');
    setSessionSlowestLap('');
    setSessionLapCount('');
    setSessionTiresUsed('');
    setSessionNotes('');
    setSessionModalOpen(true);
  };

  const openEditSessionModal = (session: TrackSession) => {
    setEditingSession(session);
    setSessionVenueName(session.venueName);
    setSessionDate(toDateInputValue(session.sessionDate));
    setSessionEventType(session.eventType);
    setSessionEventName(session.eventName ?? '');
    setSessionBestLap(session.bestLapTimeDisplay || '');
    setSessionSlowestLap(session.slowestLapTimeDisplay || '');
    setSessionLapCount(session.lapCount != null ? String(session.lapCount) : '');
    setSessionTiresUsed(session.tiresUsed ?? '');
    setSessionNotes(session.notes ?? '');
    setSessionModalOpen(true);
  };

  const closeSessionModal = () => {
    setSessionModalOpen(false);
    setEditingSession(null);
  };

  const handleSaveSession = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!existing || !sessionVenueName.trim()) return;
    if (!sessionDate.trim()) return;
    setSessionSubmitting(true);
    try {
      const bestSec = parseLapTimeDisplay(sessionBestLap);
      const slowSec = parseLapTimeDisplay(sessionSlowestLap);
      const lapCountNum = sessionLapCount.trim() ? parseInt(sessionLapCount.replace(/,/g, ''), 10) : null;
      const body = {
        venueName: sessionVenueName.trim(),
        sessionDate: sessionDate.trim(),
        eventType: sessionEventType,
        eventName: sessionEventName.trim() || undefined,
        bestLapTimeSeconds: bestSec ?? null,
        slowestLapTimeSeconds: slowSec ?? null,
        lapCount: Number.isNaN(lapCountNum as number) ? null : lapCountNum,
        tiresUsed: sessionTiresUsed.trim() || undefined,
        notes: sessionNotes.trim() || undefined,
      };
      if (editingSession) {
        await garageApi.updateSession(editingSession.id, body);
      } else {
        await garageApi.createSession(existing.id, body);
      }
      await refreshGarage();
      closeSessionModal();
    } finally {
      setSessionSubmitting(false);
    }
  };

  const openAddModModal = () => {
    setEditingMod(null);
    setModCategory(GARAGE_MOD_CATEGORIES[0]);
    setModTitle('');
    setModDetails('');
    setModDateCompleted('');
    setModImageEntries(Array(MAX_MOD_IMAGES).fill(null).map(() => ({ preview: '', caption: '', url: '' })));
    setModModalOpen(true);
  };

  const openEditModModal = (mod: GarageMod) => {
    setEditingMod(mod);
    setModCategory(mod.category);
    setModTitle(mod.title);
    setModDetails(mod.details ?? '');
    setModDateCompleted(toDateInputValue(mod.dateCompleted));
    const entries: ModImageEntry[] = mod.images?.slice(0, MAX_MOD_IMAGES).map((img) => ({
      preview: isYoutubeUrl(img.url) ? getYoutubeThumbnail(img.url) : img.url,
      caption: img.caption ?? '',
      url: img.url,
    })) ?? [];
    while (entries.length < MAX_MOD_IMAGES) {
      entries.push({ preview: '', caption: '', url: '' });
    }
    setModImageEntries(entries);
    setModModalOpen(true);
  };

  const closeModModal = () => {
    setModModalOpen(false);
    setEditingMod(null);
    setExpandedModMedia(null);
  };

  const handleSaveMod = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!existing || !modTitle.trim()) return;
    setModSubmitting(true);
    try {
      const filesToUpload = modImageEntries.filter((x) => x.file).map((x) => x.file!);
      let uploadedPaths: string[] = [];
      if (filesToUpload.length > 0) {
        const { filenames } = await uploadApi.garage(filesToUpload);
        uploadedPaths = filenames;
      }
      let pathIndex = 0;
      const images = modImageEntries
        .filter((entry) => entry.url || entry.file)
        .map((entry) => {
          const url = entry.url ?? (entry.file ? uploadedPaths[pathIndex++] : '');
          return { imageUrl: url, caption: entry.caption.trim() || null };
        })
        .filter((img) => img.imageUrl);
      const body = {
        category: modCategory,
        title: modTitle.trim(),
        details: modDetails.trim() || undefined,
        dateCompleted: modDateCompleted || undefined,
        images,
      };
      if (editingMod) {
        await garageApi.updateMod(editingMod.id, body);
      } else {
        await garageApi.createMod(existing.id, body);
      }
      await refreshGarage();
      closeModModal();
    } finally {
      setModSubmitting(false);
    }
  };

  const addModImages = (files: FileList | null) => {
    if (!files?.length) return;
    const list = Array.from(files).slice(0, MAX_MOD_IMAGES - modImageEntries.filter((e) => e.preview || e.url).length);
    setModImageEntries((prev) => {
      const next = [...prev];
      let idx = 0;
      for (const f of list) {
        while (idx < next.length && (next[idx].preview || next[idx].url)) idx++;
        if (idx >= next.length) break;
        next[idx] = { preview: URL.createObjectURL(f), caption: '', file: f };
        idx++;
      }
      return next;
    });
  };

  const removeModImage = (index: number) => {
    const entry = modImageEntries[index];
    if (entry?.preview?.startsWith('blob:')) URL.revokeObjectURL(entry.preview);
    setModImageEntries((prev) => prev.map((e, i) => (i === index ? { preview: '', caption: '', url: '' } : e)));
  };

  const setModImageCaption = (index: number, caption: string) => {
    setModImageEntries((prev) => prev.map((e, i) => (i === index ? { ...e, caption } : e)));
  };

  const setModYoutubeUrl = (index: number, value: string) => {
    const trimmed = value.trim();
    if (!trimmed) {
      setModImageEntries((prev) => prev.map((e, i) => (i === index ? { preview: '', caption: '', url: '' } : e)));
      return;
    }
    if (isYoutubeUrl(trimmed)) {
      setModImageEntries((prev) => prev.map((e, i) =>
        i === index ? { ...e, url: trimmed, preview: getYoutubeThumbnail(trimmed), file: undefined } : e
      ));
    }
  };

  const garageModsList = (existing?.garageMods ?? []).slice().sort((a, b) => {
    const dA = a.dateCompleted || '';
    const dB = b.dateCompleted || '';
    return dB.localeCompare(dA);
  });

  const garageSessionsList = (existing?.trackSessions ?? []).slice().sort((a, b) => {
    const typeA = a.eventType || '';
    const typeB = b.eventType || '';
    if (typeA !== typeB) return typeA.localeCompare(typeB);
    return (b.sessionDate || '').localeCompare(a.sessionDate || '');
  });
  const selectedMake = carMakes.find((carMake) => carMake.name.toLowerCase() === make.trim().toLowerCase());
  const makeSelectValue = selectedMake?.name ?? (make ? CURRENT_MAKE_VALUE : '');

  if (!isAuthenticated || !user) {
    return (
      <div className={styles.page}>
        <div className={styles.gateCard}>
          <p className={styles.signInPrompt}>
            Create an account to start your garage, log photos and specs, and keep a living record of your build.
          </p>
          <div className={styles.gateActions}>
            <Link
              to="/create-account"
              className={styles.cta}
              onClick={() => trackEvent('cta_click', { context: 'garage_car_form_gate', label: 'create_account' })}
            >
              Create account
            </Link>
            <Link
              to="/login"
              className={styles.secondaryCta}
              onClick={() => trackEvent('cta_click', { context: 'garage_car_form_gate', label: 'sign_in' })}
            >
              Sign in
            </Link>
          </div>
        </div>
      </div>
    );
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (imageEntries.length === 0) return;
    const yearNum = year ? parseInt(String(year), 10) : undefined;
    const filesToUpload = imageEntries.filter((e) => e.file).map((e) => e.file!);
    let uploadedPaths: string[] = [];
    if (filesToUpload.length > 0) {
      const { filenames } = await uploadApi.garage(filesToUpload);
      uploadedPaths = filenames;
    }
    let pathIndex = 0;
    const images = imageEntries.map((entry) => ({
      url: entry.url ?? uploadedPaths[pathIndex++] ?? '',
      caption: entry.caption.trim() || undefined,
    })).filter((img) => img.url);
    const performanceSpecs = performanceSpecsFromForm(spec0To60, specHp, torque, quarterMile, topSpeed, curbWeight);
    if (engineType.trim()) performanceSpecs['Engine Type'] = engineType.trim();
    if (transmission.trim()) performanceSpecs['Transmission'] = transmission.trim();
    const currentOdometerNum = currentOdometer.trim() ? parseInt(currentOdometer.replace(/,/g, ''), 10) : undefined;
    const averageMilesNum = averageMilesPerMonth.trim() ? parseInt(averageMilesPerMonth.replace(/,/g, ''), 10) : undefined;

    setSubmitting(true);
    try {
      if (isEdit && existing) {
        await updateGarageCar(existing.id, {
          name: name || undefined,
          car: {
            make,
            model,
            year: yearNum,
            bodyStyle: bodyStyle || undefined,
            powertrainType: powertrainType || undefined,
            performanceType: performanceType || undefined,
            trim: trim || undefined,
          },
          year: yearNum,
          trim: trim || undefined,
          engine: engine || undefined,
          performanceSpecs,
          colorCode: colorCode || undefined,
          colorName: colorName || undefined,
          vin: vin || undefined,
          currentOdometer: currentOdometerNum,
          averageMilesPerMonth: averageMilesNum,
          maintenanceRemindersEnabled,
          images,
          isPastCar,
          acquiredDate: acquiredDate || undefined,
          soldDate: isPastCar ? (soldDate || undefined) : null,
          forSale,
          isPrimary,
          // Always pass sale fields when forSale so they are sent (use state values, not undefined)
          saleSellingDetails: forSale ? saleSellingDetails : undefined,
          saleCarCondition: forSale ? saleCarCondition : undefined,
          saleCarfaxLink: forSale ? saleCarfaxLink : undefined,
          saleCarFeatures: forSale ? saleCarFeatures : undefined,
          salePrice: forSale ? salePrice : undefined,
          saleMilesDriven: forSale && saleMilesDriven.trim() ? parseInt(saleMilesDriven.replace(/,/g, ''), 10) : undefined,
        });
        navigate(`/garage/${existing.id}`, { state: returnNavState });
      } else {
        const car = await addGarageCar({
          ownerId: user.id,
          name: name || undefined,
          car: {
            make,
            model,
            year: yearNum,
            bodyStyle: bodyStyle || undefined,
            powertrainType: powertrainType || undefined,
            performanceType: performanceType || undefined,
            trim: trim || undefined,
          },
          year: yearNum,
          trim: trim || undefined,
          engine: engine || undefined,
          performanceSpecs,
          colorCode: colorCode || undefined,
          colorName: colorName || undefined,
          vin: vin || undefined,
          currentOdometer: currentOdometerNum,
          averageMilesPerMonth: averageMilesNum,
          maintenanceRemindersEnabled,
          images,
          photos: images.map((i) => i.url),
          isPastCar,
          acquiredDate: acquiredDate || undefined,
          soldDate: isPastCar ? (soldDate || undefined) : undefined,
          forSale,
          isPrimary,
          saleSellingDetails: forSale ? saleSellingDetails || undefined : undefined,
          saleCarCondition: forSale ? saleCarCondition || undefined : undefined,
          saleCarfaxLink: forSale ? saleCarfaxLink || undefined : undefined,
          saleCarFeatures: forSale ? saleCarFeatures || undefined : undefined,
          salePrice: forSale ? salePrice || undefined : undefined,
          saleMilesDriven: forSale && saleMilesDriven.trim() ? parseInt(saleMilesDriven.replace(/,/g, ''), 10) : undefined,
        });
        const isFirstGarage = myGarageCount === 0;
        trackEvent('garage_creation_completed', {
          first_garage: isFirstGarage,
          image_count: images.length,
          for_sale: forSale,
          is_primary: isPrimary,
        });
        if (isFirstGarage) {
          trackEvent('first_garage_completed', {
            image_count: images.length,
          });
        }
        navigate(`/garage/${car.id}`, { state: returnNavState });
      }
    } finally {
      setSubmitting(false);
    }
  };

  const addFiles = (files: FileList | null) => {
    if (!files?.length) return;
    const list = Array.from(files).slice(0, MAX_IMAGES - imageEntries.length);
    setImageEntries((prev) => [
      ...prev,
      ...list.map((f) => ({
        preview: URL.createObjectURL(f),
        caption: '',
        file: f,
      })),
    ]);
  };
  const moveImage = (index: number, dir: -1 | 1) => {
    const next = index + dir;
    if (next < 0 || next >= imageEntries.length) return;
    setImageEntries((prev) => {
      const copy = [...prev];
      [copy[index], copy[next]] = [copy[next], copy[index]];
      return copy;
    });
  };
  const removeImage = (index: number) => {
    const entry = imageEntries[index];
    if (entry?.preview && entry.preview.startsWith('blob:')) URL.revokeObjectURL(entry.preview);
    setImageEntries((prev) => prev.filter((_, i) => i !== index));
  };
  const setCaption = (index: number, caption: string) => {
    setImageEntries((prev) => prev.map((e, i) => (i === index ? { ...e, caption } : e)));
  };

  return (
    <div className={styles.page}>
      <h1>{isEdit ? 'Edit car' : 'Add car to garage'}</h1>
      <form onSubmit={handleSubmit} className={styles.form} onFocusCapture={() => trackGarageStart('form_focus')}>
        <section className={styles.section}>
          <h2>Basic info</h2>
          <label>Nickname (e.g. Daily, Weekend toy)</label>
          <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Optional" />
          <label>
            Make *
            {(useCustomMake || carMakesError) ? (
              <>
                {!carMakesError && (
                  <button
                    type="button"
                    className={styles.selectMakeLink}
                    onClick={() => {
                      const matchedMake = carMakes.find((carMake) => carMake.name.toLowerCase() === make.trim().toLowerCase());
                      setMake(matchedMake?.name ?? '');
                      setUseCustomMake(false);
                    }}
                  >
                    select make
                  </button>
                )}
                <input value={make} onChange={(e) => setMake(e.target.value)} required placeholder="e.g. Porsche" />
              </>
            ) : (
              <select
                value={makeSelectValue}
                onChange={(e) => {
                  if (e.target.value === OTHER_MAKE_VALUE) {
                    setMake('');
                    setUseCustomMake(true);
                    return;
                  }
                  if (e.target.value === CURRENT_MAKE_VALUE) return;
                  setMake(e.target.value);
                }}
                className={styles.select}
                disabled={carMakesLoading}
                required
              >
                <option value="">{carMakesLoading ? 'Loading makes...' : '— Select make —'}</option>
                {make && !selectedMake && <option value={CURRENT_MAKE_VALUE}>{make}</option>}
                {carMakes.map((carMake) => (
                  <option key={carMake.id} value={carMake.name}>{carMake.name}</option>
                ))}
                <option value={OTHER_MAKE_VALUE}>Other</option>
              </select>
            )}
          </label>
          {carMakesError && <p className={styles.hint}>Could not load car makes. Enter the make manually for now.</p>}
          <label>Model *</label>
          <input value={model} onChange={(e) => setModel(e.target.value)} required placeholder="e.g. 911" />
          <label>Trim</label>
          <input value={trim} onChange={(e) => setTrim(e.target.value)} placeholder="e.g. Carrera S" />
          <label>Year</label>
          <input type="number" value={year} onChange={(e) => setYear(e.target.value)} placeholder="e.g. 2021" min="1900" max="2030" />
          <label>Car type</label>
          <select value={bodyStyle} onChange={(e) => setBodyStyle(e.target.value)} className={styles.select}>
            {CAR_TYPES.map((opt) => (
              <option key={opt} value={opt}>{opt || 'Select car type'}</option>
            ))}
          </select>
          <label>Powertrain</label>
          <select
            value={powertrainType}
            onChange={(e) => setPowertrainType(e.target.value as (typeof POWERTRAIN_TYPES)[number])}
            className={styles.select}
          >
            {POWERTRAIN_TYPES.map((opt) => (
              <option key={opt} value={opt}>{opt.charAt(0).toUpperCase() + opt.slice(1)}</option>
            ))}
          </select>
          <label>Performance type</label>
          <select value={performanceType} onChange={(e) => setPerformanceType(e.target.value)} className={styles.select}>
            {PERFORMANCE_TYPES.map((opt) => (
              <option key={opt} value={opt}>{opt || 'Select performance type'}</option>
            ))}
          </select>
          <label className={styles.checkLabel}>
            <input type="checkbox" checked={isPrimary} onChange={(e) => setIsPrimary(e.target.checked)} />
            Set as primary car
          </label>
        </section>

        <section className={styles.section}>
          <h2>Engine & performance</h2>
          <label>Engine</label>
          <input value={engine} onChange={(e) => setEngine(e.target.value)} placeholder="e.g. 3.0L Twin-Turbo Flat-6" />
          <label>Engine type</label>
          <select value={engineType} onChange={(e) => setEngineType(e.target.value)} className={styles.select}>
            {ENGINE_TYPES.map((opt) => (
              <option key={opt} value={opt}>{opt || 'Select engine type'}</option>
            ))}
          </select>
          <label>HP</label>
          <input value={specHp} onChange={(e) => setSpecHp(e.target.value)} placeholder="e.g. 443" />
          <label>Torque (lb-ft)</label>
          <input value={torque} onChange={(e) => setTorque(e.target.value)} placeholder="e.g. 480 lb-ft" />
          <label>Transmission</label>
          <input value={transmission} onChange={(e) => setTransmission(e.target.value)} placeholder="e.g. 7-speed PDK" />
          <label>Curb weight (lbs)</label>
          <input value={curbWeight} onChange={(e) => setCurbWeight(e.target.value)} placeholder="e.g. 3300" />
          <label>Performance specs</label>
          <div className={styles.specRow}>
            <span className={styles.specLabel}>0-60</span>
            <input
              value={spec0To60}
              onChange={(e) => setSpec0To60(e.target.value)}
              placeholder="e.g. 3.5s"
            />
          </div>
          <div className={styles.specRow}>
            <span className={styles.specLabel}>1/4 mile</span>
            <input
              value={quarterMile}
              onChange={(e) => setQuarterMile(e.target.value)}
              placeholder="e.g. 11.2s @ 125mph"
            />
          </div>
          <div className={styles.specRow}>
            <span className={styles.specLabel}>Top speed (mph)</span>
            <input
              value={topSpeed}
              onChange={(e) => setTopSpeed(e.target.value)}
              placeholder="e.g. 191 mph"
            />
          </div>
        </section>

        {isEdit && existing && (
          <section className={styles.section}>
            <h2>Mods</h2>
            <p className={styles.hint}>Add individual mods with category, title, details, and photos or youtube link.</p>
            <button type="button" className={styles.addPhotosBtn} onClick={openAddModModal}>
              Add Mod
            </button>
            {garageModsList.length > 0 && (
              <ul className={styles.modsList}>
                {garageModsList.map((mod) => (
                  <li key={mod.id}>
                    <button type="button" className={styles.modLink} onClick={() => openEditModModal(mod)}>
                      {mod.title}
                    </button>
                    {mod.dateCompleted && (
                      <span className={styles.modDate}> — {formatDateLocal(mod.dateCompleted)}</span>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </section>
        )}

        <section className={styles.section}>
          <h2>Maintenance profile</h2>
          <label>Current odometer</label>
          <input
            type="number"
            value={currentOdometer}
            onChange={(e) => setCurrentOdometer(e.target.value)}
            placeholder="e.g. 24500"
            min={0}
          />
          <label>Average miles per month</label>
          <input
            type="number"
            value={averageMilesPerMonth}
            onChange={(e) => setAverageMilesPerMonth(e.target.value)}
            placeholder="e.g. 900"
            min={0}
          />
          <p className={styles.hint}>RideSpot uses these values to predict upcoming maintenance dates from mileage-based intervals.</p>
          <label className={styles.checkLabel}>
            <input
              type="checkbox"
              checked={maintenanceRemindersEnabled}
              onChange={(e) => setMaintenanceRemindersEnabled(e.target.checked)}
            />
            Email maintenance reminders
          </label>
        </section>

        <section className={styles.section}>
          <h2>Color & VIN</h2>
          <label>Color name</label>
          <input value={colorName} onChange={(e) => setColorName(e.target.value)} placeholder="e.g. Aventurine Green" />
          <label>Color code</label>
          <input value={colorCode} onChange={(e) => setColorCode(e.target.value)} placeholder="Optional" />
          <label>VIN (optional)</label>
          <input value={vin} onChange={(e) => setVin(e.target.value)} placeholder="Optional" />
        </section>

        <section className={styles.section}>
          <h2>Photos (up to {MAX_IMAGES}, first is cover, max 100MB at a time).</h2>
          <span className={styles.hint}>Large images will be resized to save disk space.</span>
          <input
            ref={photoInputRef}
            type="file"
            accept="image/jpeg,image/png,image/gif,image/webp"
            multiple
            className={styles.fileInput}
            onChange={(e) => {
              addFiles(e.target.files);
              e.target.value = '';
            }}
          />
          {imageEntries.length < MAX_IMAGES && (
            <button
              type="button"
              className={styles.addPhotosBtn}
              onClick={() => {
                trackGarageStart('photo_picker');
                photoInputRef.current?.click();
              }}
            >
              Add photos ({imageEntries.length}/{MAX_IMAGES})
            </button>
          )}
          {imageEntries.length > 0 && (
            <div className={styles.imageList}>
              {imageEntries.map((entry, i) => (
                <div key={i} className={styles.imageEntry}>
                  <div className={styles.imageThumbWrap}>
                    <img src={entry.preview} alt="" className={styles.imageThumb} />
                    <div className={styles.imageActions}>
                      <button type="button" onClick={() => moveImage(i, -1)} disabled={i === 0} aria-label="Move up">
                        ↑
                      </button>
                      <button type="button" onClick={() => moveImage(i, 1)} disabled={i === imageEntries.length - 1} aria-label="Move down">
                        ↓
                      </button>
                      <button type="button" className={styles.removePhoto} onClick={() => removeImage(i)} aria-label="Remove">
                        ×
                      </button>
                    </div>
                    {i === 0 && <span className={styles.coverBadge}>Cover</span>}
                  </div>
                  <input
                    type="text"
                    value={entry.caption}
                    onChange={(e) => setCaption(i, e.target.value)}
                    placeholder="Caption for this photo"
                    className={styles.captionInput}
                  />
                </div>
              ))}
            </div>
          )}
        </section>

        {isEdit && existing && (
          <section className={styles.section}>
            <h2>Track Sessions</h2>
            <p className={styles.hint}>Record track days, time trials, HPDE, racing, autocross, or training sessions with lap times and venue.</p>
            <button type="button" className={styles.addPhotosBtn} onClick={openAddSessionModal}>
              Add Session
            </button>
            {garageSessionsList.length > 0 && (
              <ul className={styles.modsList}>
                {garageSessionsList.map((session) => (
                  <li key={session.id}>
                    <button type="button" className={styles.modLink} onClick={() => openEditSessionModal(session)}>
                      {session.eventType} — {session.venueName}
                    </button>
                    {session.sessionDate && (
                      <span className={styles.modDate}> — {formatDateLocal(session.sessionDate)}</span>
                    )}
                    {session.bestLapTimeDisplay && (
                      <span className={styles.modDate}> · Best: {session.bestLapTimeDisplay}</span>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </section>
        )}

        <section className={styles.section}>
          <label className={styles.checkLabel}>
            <input
              type="checkbox"
              checked={isPastCar}
              onChange={(e) => {
                const checked = e.target.checked;
                setIsPastCar(checked);
                if (!checked) {
                  setSoldDate('');
                }
              }}
            />
            Past car (sold or no longer own)
          </label>
          {isPastCar===true && (
            <>
              <label>Acquired date</label>
              <input type="date" value={acquiredDate} onChange={(e) => setAcquiredDate(e.target.value)} />
              <label>Sold date</label>
              <input type="date" value={soldDate} onChange={(e) => setSoldDate(e.target.value)} />
            </>
          )}
        </section>

        <section className={styles.section}>
          <label className={styles.checkLabel}>
            <input type="checkbox" checked={forSale} onChange={(e) => setForSale(e.target.checked)} />
            For Sale
          </label>
          {forSale && (
            <>
              <label>Selling details</label>
              <textarea value={saleSellingDetails} onChange={(e) => setSaleSellingDetails(e.target.value)} placeholder="Describe why you're selling, highlights, etc." rows={3} />
              <label>Miles driven</label>
              <input type="number" value={saleMilesDriven} onChange={(e) => setSaleMilesDriven(e.target.value)} placeholder="e.g. 45000" min={0} />
              <label>Car condition</label>
              <input value={saleCarCondition} onChange={(e) => setSaleCarCondition(e.target.value)} placeholder="e.g. Excellent, no accidents" />
              <label>Carfax link</label>
              <input type="url" value={saleCarfaxLink} onChange={(e) => setSaleCarfaxLink(e.target.value)} placeholder="https://www.carfax.com/..." />
              <label>Car features</label>
              <textarea value={saleCarFeatures} onChange={(e) => setSaleCarFeatures(e.target.value)} placeholder="List key features, options, or modifications" rows={3} />
              <label>Price</label>
              <input value={salePrice} onChange={(e) => setSalePrice(e.target.value)} placeholder="e.g. $45,000 or OBO" />
            </>
          )}
        </section>

        <div className={styles.actions}>
          <button type="button" className={styles.cancel} onClick={() => navigate(-1)}>
            Cancel
          </button>
          <button type="submit" className={styles.submit} disabled={submitting}>
            {submitting ? 'Saving...' : isEdit ? 'Save' : 'Add car'}
          </button>
        </div>
      </form>

      {modModalOpen && (
        <div className={styles.modalOverlay} onClick={closeModModal} role="presentation">
          <div className={styles.modal} onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true">
            <h2>{editingMod ? 'Edit mod' : 'Add mod'}</h2>
            <form onSubmit={handleSaveMod} className={styles.modalForm}>
              <div className={styles.modalFormSection}>
                <div className={styles.modalFormGroup}>
                  <label htmlFor="mod-category">Category *</label>
                  <select id="mod-category" value={modCategory} onChange={(e) => setModCategory(e.target.value)} className={styles.modalInput} required>
                    {GARAGE_MOD_CATEGORIES.map((cat) => (
                      <option key={cat} value={cat}>{cat}</option>
                    ))}
                  </select>
                </div>
                <div className={styles.modalFormGroup}>
                  <label htmlFor="mod-title">Title *</label>
                  <input id="mod-title" value={modTitle} onChange={(e) => setModTitle(e.target.value)} className={styles.modalInput} required placeholder="e.g. Coilovers" />
                </div>
                <div className={styles.modalFormGroup}>
                  <label htmlFor="mod-date">Date completed</label>
                  <input id="mod-date" type="date" value={modDateCompleted} onChange={(e) => setModDateCompleted(e.target.value)} className={styles.modalInput} />
                </div>
              </div>
              <div className={styles.modalFormSection}>
                <div className={styles.modalFormGroup}>
                  <label htmlFor="mod-details">Details</label>
                  <textarea id="mod-details" value={modDetails} onChange={(e) => setModDetails(e.target.value)} className={styles.modalInput} placeholder="Optional description" rows={3} />
                </div>
              </div>
              <div className={styles.modalFormSection}>
                <label className={styles.modalFormLabel}>Images (up to {MAX_MOD_IMAGES} with captions)</label>
                <input
                  ref={modPhotoInputRef}
                  type="file"
                  accept="image/jpeg,image/png,image/gif,image/webp"
                  multiple
                  className={styles.fileInput}
                  onChange={(e) => { addModImages(e.target.files); e.target.value = ''; }}
                />
                <button type="button" className={styles.addPhotosBtn} onClick={() => modPhotoInputRef.current?.click()}>
                  Add photos
                </button>
                <div className={styles.modImageGrid}>
                  {modImageEntries.map((entry, i) => (
                    <div key={i} className={styles.modImageEntry}>
                      {(entry.preview || entry.url) ? (
                        <>
                          <div className={styles.modImagePreviewWrap}>
                            <button
                              type="button"
                              className={styles.modImageThumbButton}
                              onClick={() => setExpandedModMedia(entry.url || entry.preview || '')}
                            >
                              <img src={entry.preview || entry.url} alt="" className={styles.imageThumb} />
                              {isYoutubeUrl(entry.url ?? '') && <span className={styles.youtubeBadge}>YouTube</span>}
                              {isYoutubeUrl(entry.url ?? '') && <span className={styles.modYoutubePlayIcon} aria-hidden>▶</span>}
                            </button>
                          </div>
                          <input
                            type="text"
                            value={entry.caption}
                            onChange={(e) => setModImageCaption(i, e.target.value)}
                            placeholder="Caption"
                            className={styles.modalCaptionInput}
                          />
                          <button type="button" className={styles.removePhoto} onClick={() => removeModImage(i)} aria-label="Remove">×</button>
                        </>
                      ) : (
                        <>
                          <button type="button" className={styles.modImagePlaceholder} onClick={() => modPhotoInputRef.current?.click()}>
                            Add image {i + 1}
                          </button>
                          <input
                            type="text"
                            value=""
                            onChange={(e) => setModYoutubeUrl(i, e.target.value)}
                            placeholder="Or paste YouTube link"
                            className={styles.modalCaptionInput}
                          />
                        </>
                      )}
                    </div>
                  ))}
                </div>
              </div>
              <div className={styles.modalActions}>
                <button type="button" className={styles.cancel} onClick={closeModModal}>Cancel</button>
                <button type="submit" className={styles.submit} disabled={modSubmitting}>
                  {modSubmitting ? 'Saving...' : editingMod ? 'Save' : 'Add mod'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {sessionModalOpen && (
        <div className={styles.modalOverlay} onClick={closeSessionModal} role="presentation">
          <div className={styles.modal} onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true">
            <h2>{editingSession ? 'Edit track session' : 'Add track session'}</h2>
            <form onSubmit={handleSaveSession} className={styles.modalForm}>
              <div className={styles.modalFormSection}>
                <div className={styles.modalFormGroup}>
                  <label htmlFor="session-venue">Venue name *</label>
                  <input id="session-venue" value={sessionVenueName} onChange={(e) => setSessionVenueName(e.target.value)} className={styles.modalInput} required placeholder="e.g. Laguna Seca" />
                </div>
                <div className={styles.modalFormGroup}>
                  <label htmlFor="session-date">Session date *</label>
                  <input id="session-date" type="date" value={sessionDate} onChange={(e) => setSessionDate(e.target.value)} className={styles.modalInput} required />
                </div>
                <div className={styles.modalFormGroup}>
                  <label htmlFor="session-event-type">Event type *</label>
                  <select id="session-event-type" value={sessionEventType} onChange={(e) => setSessionEventType(e.target.value)} className={styles.modalInput} required>
                    {TRACK_SESSION_EVENT_TYPES.map((t) => (
                      <option key={t} value={t}>{t}</option>
                    ))}
                  </select>
                </div>
                <div className={styles.modalFormGroup}>
                  <label htmlFor="session-event-name">Event name</label>
                  <input id="session-event-name" value={sessionEventName} onChange={(e) => setSessionEventName(e.target.value)} className={styles.modalInput} placeholder="Optional" />
                </div>
              </div>
              <div className={styles.modalFormSection}>
                <div className={styles.modalFormGroup}>
                  <label htmlFor="session-best-lap">Best lap time (M:SS.xxx)</label>
                  <input id="session-best-lap" value={sessionBestLap} onChange={(e) => setSessionBestLap(e.target.value)} className={styles.modalInput} placeholder="e.g. 1:32.456" />
                </div>
                <div className={styles.modalFormGroup}>
                  <label htmlFor="session-slowest-lap">Slowest lap time (M:SS.xxx)</label>
                  <input id="session-slowest-lap" value={sessionSlowestLap} onChange={(e) => setSessionSlowestLap(e.target.value)} className={styles.modalInput} placeholder="e.g. 1:38.120" />
                </div>
                <div className={styles.modalFormGroup}>
                  <label htmlFor="session-lap-count">Number of laps</label>
                  <input id="session-lap-count" type="number" min={0} value={sessionLapCount} onChange={(e) => setSessionLapCount(e.target.value)} className={styles.modalInput} placeholder="Optional" />
                </div>
                <div className={styles.modalFormGroup}>
                  <label htmlFor="session-tires">Tires used</label>
                  <input id="session-tires" value={sessionTiresUsed} onChange={(e) => setSessionTiresUsed(e.target.value)} className={styles.modalInput} placeholder="e.g. Michelin Pilot Sport Cup 2" />
                </div>
                <div className={styles.modalFormGroup}>
                  <label htmlFor="session-notes">Notes</label>
                  <textarea id="session-notes" value={sessionNotes} onChange={(e) => setSessionNotes(e.target.value)} className={styles.modalInput} placeholder="Optional notes" rows={3} />
                </div>
              </div>
              <div className={styles.modalActions}>
                <button type="button" className={styles.cancel} onClick={closeSessionModal}>Cancel</button>
                <button type="submit" className={styles.submit} disabled={sessionSubmitting}>
                  {sessionSubmitting ? 'Saving...' : editingSession ? 'Save' : 'Add session'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {expandedModMedia && (
        <div
          className={styles.modMediaExpandedOverlay}
          onClick={() => setExpandedModMedia(null)}
          role="button"
          tabIndex={0}
          onKeyDown={(e) => e.key === 'Escape' && setExpandedModMedia(null)}
          aria-label="Close expanded view"
        >
          {isYoutubeUrl(expandedModMedia) ? (
            <div className={styles.modYoutubeEmbedWrap} onClick={(e) => e.stopPropagation()}>
              <iframe
                title="YouTube video"
                src={getYoutubeEmbedUrl(expandedModMedia)}
                className={styles.modYoutubeEmbed}
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              />
            </div>
          ) : (
            <img
              src={expandedModMedia}
              alt="Expanded"
              className={styles.modMediaExpandedImg}
              onClick={(e) => e.stopPropagation()}
            />
          )}
          <button
            type="button"
            className={styles.modMediaExpandedClose}
            onClick={() => setExpandedModMedia(null)}
            aria-label="Close"
          >
            ×
          </button>
        </div>
      )}
    </div>
  );
}
